/* global MT_INSTALL_AGENCIES */

import 'whatwg-fetch';

import moment from 'moment-timezone';
import PropTypes from 'prop-types'; // fetch polyfill
import React from 'react';
import {connect} from 'react-redux';
import {replace} from 'connected-react-router';
import _ from 'underscore';

import {
  getActiveAgency,
  getActiveAgencyId,
  getActiveRides,
  getActiveService,
  getActiveServiceLoad,
  getAgencyAddressList,
  getAgencySettings,
  getAvailableServices,
  getDropoffAddresses,
  getPickupAddresses,
  getDropoffArea,
  getPickupArea,
  getServiceArea,
  getRideRequest,
  getRideRequestDirections,
  getSelectableAddresses,
  getServiceEstimate,
  getServiceLoadThresholds,
} from 'ondemand/selectors';
import {fetchRegionsIfNeeded} from 'ondemand/actions/region';
import {pushAlert} from 'ondemand/actions/alert';
import {clearRideRequest, createRide, updateRideRequest} from 'ondemand/actions/ride';
import {pollServiceEstimate, cancelServiceEstimate} from 'ondemand/actions/serviceEstimate';
import {fetchAgencyAddressesIfNeeded} from 'ondemand/actions/agencyAddress';
import {fetchAgencySettingsIfNeeded} from 'common/actions/agencySettings';
import {fetchRiderAddressesIfNeeded} from 'ondemand/actions/riderAddress';
import {fetchServiceGeometriesIfNeeded} from 'ondemand/actions/serviceGeometries';
import {changeSetting} from 'common/actions/settings';
import {serviceTypes} from 'common/types/Service';
import RideRequestMap from 'ondemand/components/RideRequestMap';
import AgencyPicker from 'ondemand/containers/AgencyPicker';
import ServicePicker from 'ondemand/containers/ServicePicker';
import {iOS, android} from 'common/utils';
import mticon from 'ondemand/icons/mticon.svg';


class RideRequestPage extends React.Component {
  static displayName = 'RideRequestPage';

  static propTypes = {
    activeRides: PropTypes.arrayOf(PropTypes.object),
    activeService: PropTypes.object,
    activeServiceEstimate: PropTypes.shape({
      estimated_wait_time: PropTypes.number,
    }),
    activeServiceLoad: PropTypes.string,
    addresses: PropTypes.array,
    dropoffAddresses: PropTypes.array,
    pickupAddresses: PropTypes.array,
    dropoffArea: PropTypes.object,
    pickupArea: PropTypes.object,
    serviceArea: PropTypes.object,
    agencyAddresses: PropTypes.array,
    agencySettings: PropTypes.object,
    availableServices: PropTypes.arrayOf(PropTypes.object),
    agencies: PropTypes.objectOf(PropTypes.object),
    agency: PropTypes.object,
    agencyId: PropTypes.string,
    dispatch: PropTypes.func.isRequired,
    location: PropTypes.shape({
      query: PropTypes.shape({
        agencies: PropTypes.string,
      }),
    }),
    rideRequest: PropTypes.object,
    rideRequestDirections: PropTypes.object,
    rides: PropTypes.objectOf(PropTypes.object),
    route: PropTypes.object,
    services: PropTypes.objectOf(PropTypes.object),
    thresholds: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    seenRideShareNotification: PropTypes.bool,
    bannerShownDate: PropTypes.string,
  };

  state = {
    showRedirect: true,
  };

  componentDidMount() {
    this.props.dispatch(fetchRiderAddressesIfNeeded());
    this._handleComponentUpdate({}, this.props);
    this.props.dispatch(clearRideRequest());
  }

  componentDidUpdate(prevProps) {
    this._handleComponentUpdate(prevProps, this.props);

    // Check if we should show the Microtransit app banner
    const mtInstallAgencies = MT_INSTALL_AGENCIES;
    const lastShownDate = this.props.bannerShownDate ? moment(this.props.bannerShownDate) : null;
    const banner = (
      <img
        style={{
          marginRight: '35px',
          zoom: 0.32,
          backgroundColor: 'transparent',
        }}
        src={mticon}
      />
    );
    if (
      this.props.agency &&
      _.contains(mtInstallAgencies, this.props.agency.agencyId) &&
      this.props.activeService &&
      this.state.showRedirect &&
      (!lastShownDate || lastShownDate.isBefore(moment().subtract(7, 'days'))) &&
      (iOS || android)
    ) {
      this.props.dispatch(pushAlert({
        title: 'Transloc App now available!',
        subtitle: 'Download now for a better experience booking your on-demand rides.',
        level: 'banner',
        icon: banner,
        onClick: () => {
          const url = iOS
            ? 'itms-apps://itunes.apple.com/us/app/microtransit/id1280444930'
            : 'https://play.google.com/store/apps/details?id=com.transloc.microtransit';
          window.location.replace(url);
        },
        onClose: () => {
          this.props.dispatch(changeSetting('bannerShownDate', moment().toISOString()));
        },
      }));
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({showRedirect: false});
    }
  }

  componentWillUnmount() {
    this.props.dispatch(cancelServiceEstimate());
  }

  _handleComponentUpdate = (oldProps, newProps) => {
    if (newProps.agencyId && newProps.agencyId !== oldProps.agencyId) {
      this._fetchAgencyData(newProps.agencyId);
    }

    if (newProps.activeService && newProps.activeService !== oldProps.activeService) {
      this.props.dispatch(
        fetchServiceGeometriesIfNeeded(newProps.agencyId, newProps.activeService.service_id),
      );

      if (oldProps.activeService) {
        this.props.dispatch(cancelServiceEstimate());
      }

      if (newProps.activeService.service_type !== serviceTypes.INADVANCE) {
        this.props.dispatch(
          pollServiceEstimate(newProps.agencyId, newProps.activeService.service_id),
        );
      }
    }
  };

  _fetchAgencyData = (agencyId) => {
    this.props.dispatch(fetchAgencyAddressesIfNeeded(agencyId));
    this.props.dispatch(fetchAgencySettingsIfNeeded(agencyId));
    this.props.dispatch(fetchRegionsIfNeeded(agencyId));
  };

  _handleRideRequestChange = (rideRequest) => {
    this.props.dispatch(updateRideRequest(rideRequest));
  };

  _handleRideRequestClear = () => {
    this.props.dispatch(changeSetting('serviceId', null));
    this.props.dispatch(clearRideRequest());
  };

  _handleRideRequestSubmit = () => {
    this.props
      .dispatch(
        createRide(
          this.props.agencyId,
          this.props.activeService.service_id,
          this.props.rideRequest,
          this.props.rideRequestDirections,
          this.props.activeServiceEstimate
            ? this.props.activeServiceEstimate.estimated_wait_time
            : null
        )
      )
      .then(ride => {
        if (ride && ride.rideId) {
          this.props.dispatch(replace(`/rides/${ride.rideId}`));
        }
      });
  };

  _handleRideShareConfirm = () => {
    this.props.dispatch(changeSetting('seenRideShareNotification', true));
  };

  render() {
    const service = this.props.activeService;

    const inService = service && service.inServiceAt(moment.tz(this.props.agency.timezone));
    const allowBookForNow = !!service && _.all([
      service.service_type === serviceTypes.ONDEMAND,
      inService,
      !_.size(this.props.activeRides),
    ]);
    const allowBookForLater = !!service && _.any([
      service.service_type === serviceTypes.INADVANCE,
      service.in_advance_enabled,
    ]);

    let availabilityMessage = null;
    if (!allowBookForNow && allowBookForLater) {
      if (service.service_type === serviceTypes.ONDEMAND) {
        if (!inService) {
          availabilityMessage =
            'This service is not currently available for immediate rides, ' +
            'but you can book a ride for a later time.';
        } else if (_.size(this.props.activeRides)) {
          availabilityMessage =
            'Immediate rides are disabled while another ride is taking place, ' +
            'but you can book a ride for a later time.';
        }
      }
    }

    return (
      <div data-id={'ride-request-page-container'} style={{height: '100%'}}>
        <AgencyPicker />
        <ServicePicker />
        <RideRequestMap
          addresses={this.props.addresses}
          dropoffAddresses={this.props.dropoffAddresses}
          pickupAddresses={this.props.pickupAddresses}
          dropoffArea={this.props.dropoffArea}
          pickupArea={this.props.pickupArea}
          serviceArea={this.props.serviceArea}
          agency={this.props.agency}
          agencyAddresses={this.props.agencyAddresses}
          agencySettings={this.props.agencySettings}
          allowBookForNow={allowBookForNow}
          allowBookForLater={allowBookForLater}
          center={(this.props.agencies[this.props.agencyId] || {}).position}
          disabled={!this.props.activeService}
          rideRequest={this.props.rideRequest}
          rideRequestDirections={this.props.rideRequestDirections}
          service={this.props.activeService}
          serviceLoad={this.props.activeServiceLoad}
          onRideRequestChange={this._handleRideRequestChange}
          onRideRequestClear={this._handleRideRequestClear}
          onRideRequestSubmit={this._handleRideRequestSubmit}
          serviceAvailabilityMessage={availabilityMessage}
          thresholds={this.props.thresholds}
          timezone={this.props.agency && this.props.agency.timezone}
          user={this.props.user}
          showRideShareNotification={!this.props.seenRideShareNotification}
          onRideShareConfirm={this._handleRideShareConfirm}
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  activeRides: getActiveRides(state),
  activeService: getActiveService(state),
  activeServiceEstimate: getServiceEstimate(state),
  activeServiceLoad: getActiveServiceLoad(state),
  addresses: getSelectableAddresses(state),
  dropoffAddresses: getDropoffAddresses(state),
  pickupAddresses: getPickupAddresses(state),
  dropoffArea: getDropoffArea(state),
  pickupArea: getPickupArea(state),
  serviceArea: getServiceArea(state),
  isFetchingAreas: state.serviceGeometries.isFetching,
  agencyAddresses: getAgencyAddressList(state),
  agencySettings: getAgencySettings(state),
  availableServices: getAvailableServices(state),
  agencies: state.agency.items || {},
  agency: getActiveAgency(state),
  agencyId: getActiveAgencyId(state) || null,
  regions: state.region.items || [],
  rideRequest: getRideRequest(state) || {},
  rideRequestDirections: getRideRequestDirections(state),
  rides: state.ride.items || {},
  thresholds: getServiceLoadThresholds(),
  user: state.user.user || {},
  seenRideShareNotification: state.settings.seenRideShareNotification || false,
  bannerShownDate: state.settings.bannerShownDate || null,
});

export default connect(mapStateToProps)(RideRequestPage);



// WEBPACK FOOTER //
// ./src/ondemand/containers/RideRequestPage.js